import type { ViewHook } from '../helpers/utils'

type WebAwesomeDialog = HTMLElement & {
  open: boolean
}

type ScanResult = Readonly<{
  data: string
}>

type ScannerOptions = Readonly<{
  highlightScanRegion: true
}>

type ScannerInstance = Readonly<{
  destroy: () => void
  start: () => Promise<void>
  stop: () => void
}>

type ScannerConstructor = new (
  video: HTMLVideoElement,
  onDecode: (result: ScanResult) => void,
  options: ScannerOptions
) => ScannerInstance

type ScannerModule = Readonly<{
  default: ScannerConstructor
}>

type ScannerLoader = () => Promise<ScannerModule>

type ScannerConfig = Readonly<{
  dialog: WebAwesomeDialog
  eventOnScan: string
  trigger: HTMLElement
  video: HTMLVideoElement
}>

type ScannerState =
  | Readonly<{ type: 'loading' }>
  | Readonly<{ type: 'inactive' }>
  | Readonly<{
      type: 'ready'
      cleanup: () => void
      scanner: ScannerInstance
    }>
  | Readonly<{ type: 'destroyed' }>

type QrCodeScannerHook = ViewHook & {
  qrScannerState?: ScannerState
}

const loadScanner: ScannerLoader = async () => import('../vendor/qr-scanner')

const readScannerConfig = (el: HTMLElement): ScannerConfig | undefined => {
  const trigger = el.querySelector<HTMLElement>('[data-part="trigger"]')
  const dialog = el.querySelector<WebAwesomeDialog>('[data-part="dialog"]')
  const video = el.querySelector<HTMLVideoElement>('[data-part="video"]')
  const eventOnScan = el.dataset.eventOnScan

  return trigger && dialog && video && eventOnScan
    ? { dialog, eventOnScan, trigger, video }
    : undefined
}

const wasDestroyed = (hook: QrCodeScannerHook): boolean =>
  hook.qrScannerState?.type === 'destroyed'

const setupScanner = (
  hook: QrCodeScannerHook,
  Scanner: ScannerConstructor,
  { dialog, eventOnScan, trigger, video }: ScannerConfig
): Extract<ScannerState, { type: 'ready' }> => {
  let generation = 0
  let activeGeneration: number | undefined

  const isCurrentGeneration = (candidate: number): boolean =>
    generation === candidate && hook.qrScannerState?.type === 'ready'

  const invalidateSession = (): void => {
    generation = generation + 1
    activeGeneration = undefined
  }

  const scanner = new Scanner(
    video,
    (result) => {
      const resultGeneration = activeGeneration

      if (
        resultGeneration === undefined ||
        !isCurrentGeneration(resultGeneration)
      ) {
        return
      }

      activeGeneration = undefined
      hook.pushEvent(
        eventOnScan,
        { status: 'success', identifier: result.data },
        () => {
          if (!isCurrentGeneration(resultGeneration)) {
            return
          }

          invalidateSession()
          scanner.stop()
          dialog.open = false
        }
      )
    },
    { highlightScanRegion: true }
  )

  const startSession = (): void => {
    generation = generation + 1
    const sessionGeneration = generation
    activeGeneration = sessionGeneration

    scanner.start().catch(() => {
      if (isCurrentGeneration(sessionGeneration)) {
        invalidateSession()
      }
    })
  }

  const handleClick = (): void => {
    dialog.open = true
  }

  const handleAfterShow = (event: Event): void => {
    if (event.target === dialog) {
      startSession()
    }
  }

  const handleHide = (event: Event): void => {
    if (event.target === dialog) {
      invalidateSession()
      scanner.stop()
    }
  }

  trigger.addEventListener('click', handleClick)
  dialog.addEventListener('wa-after-show', handleAfterShow)
  dialog.addEventListener('wa-hide', handleHide)

  return {
    type: 'ready',
    scanner,
    cleanup: () => {
      trigger.removeEventListener('click', handleClick)
      dialog.removeEventListener('wa-after-show', handleAfterShow)
      dialog.removeEventListener('wa-hide', handleHide)
      invalidateSession()
    }
  }
}

export const createFacadeQrCodeScanner = (
  scannerLoader: ScannerLoader = loadScanner
) => ({
  async mounted(this: QrCodeScannerHook): Promise<void> {
    this.qrScannerState = { type: 'loading' }

    const { default: Scanner } = await scannerLoader()

    if (wasDestroyed(this)) {
      return
    }

    const config = readScannerConfig(this.el)
    this.qrScannerState = config
      ? setupScanner(this, Scanner, config)
      : { type: 'inactive' }
  },

  destroyed(this: QrCodeScannerHook): void {
    const state = this.qrScannerState
    this.qrScannerState = { type: 'destroyed' }

    if (state?.type === 'ready') {
      state.cleanup()
      state.scanner.stop()
      state.scanner.destroy()
    }
  }
})

export const FacadeQrCodeScanner = createFacadeQrCodeScanner()
